import { useQuery } from "@tanstack/react-query";
import { httpClient } from "@coworker/http-client";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";

type RelatedTaskLocation = {
  slid: string;
  custom: string;
};

export type RelatedTasksRequest = {
  taskTypeId: string;
  articleNo: string;
  locale: string;
  issueValue?: string;
  location?: RelatedTaskLocation;
};

type RelatedTasksResponse = {
  tasks: Task[];
  summary: {
    summaryText: string;
    descriptionGists: string[];
    totalTasks: number;
    issueGroups: {
      issueValue: string;
      totalCount: number;
      locations: { location: string; count: number }[];
    }[];
    llmSummary?: string;
  } | null;
};

function normalizeRelatedTask(task: Record<string, unknown>): Task {
  const id =
    typeof task["id"] === "string"
      ? task["id"]
      : typeof task["taskId"] === "string"
        ? task["taskId"]
        : typeof task["task_id"] === "string"
          ? task["task_id"]
          : "";

  return { ...task, id } as Task;
}

export async function getRelatedTasks(request: RelatedTasksRequest) {
  const response = await httpClient.tasks.post<
    | Record<string, unknown>[]
    | {
        tasks?: Record<string, unknown>[];
        summary?: RelatedTasksResponse["summary"];
      }
  >("/tasks-v2/related", {
    body: request,
  });

  const tasks = Array.isArray(response)
    ? response
    : Array.isArray(response.tasks)
      ? response.tasks
      : [];

  const normalizedTasks = tasks
    .map(normalizeRelatedTask)
    .filter(({ id }) => Boolean(id));

  const summary =
    !Array.isArray(response) && response.summary ? response.summary : null;

  return { tasks: normalizedTasks, summary };
}

export function useRelatedTasks(request: RelatedTasksRequest | null) {
  return useQuery({
    enabled: request !== null,
    retry: false,
    staleTime: 300_000,
    gcTime: 1_800_000,
    refetchOnWindowFocus: false,
    queryKey: [
      "tasks_v2",
      "related",
      request?.taskTypeId,
      request?.articleNo,
      request?.locale,
    ],
    queryFn: () => getRelatedTasks(request!),
  });
}
